/**
 * TypeScript mirror of server/internal/protocol/protocol.go.
 *
 * Two conventions carried over from the server, both of which matter:
 *
 *  - Multipliers are integers in hundredths. 250 is 2.50x. Never a float, so a
 *    displayed multiplier and a paid multiplier can never disagree by a rounding
 *    error.
 *  - Money is a decimal *string*. Parsing it into a JS number would silently
 *    round an eight-decimal crypto balance, so it is formatted for display and
 *    otherwise passed through untouched.
 *
 * A third convention arrived with parallel betting: every message about a bet
 * carries a `slot`. It is the only identifier the client may name -- ids, rounds,
 * multipliers and payouts all come from the server -- and it exists because with
 * two live stakes "cash out" on its own is ambiguous.
 */

export const ServerMessage = {
  RoundBetting: 'round.betting',
  RoundStart: 'round.start',
  RoundTick: 'round.tick',
  RoundCrash: 'round.crash',
  BetPlaced: 'bet.placed',
  BetCashout: 'bet.cashout',
  BetAccepted: 'bet.accepted',
  BetCanceled: 'bet.canceled',
  CashedOut: 'bet.cashedOut',
  WalletUpdate: 'wallet.update',
  Snapshot: 'game.snapshot',
  Error: 'error',
  Pong: 'pong',
} as const;

export const ClientMessage = {
  BetPlace: 'bet.place',
  BetCancel: 'bet.cancel',
  BetCashOut: 'bet.cashOut',
  Ping: 'ping',
} as const;

export const ErrorCode = {
  Unauthorized: 'unauthorized',
  BettingClosed: 'betting_closed',
  RoundNotRunning: 'round_not_running',
  AlreadyBet: 'already_bet',
  NoActiveBet: 'no_active_bet',
  AlreadyCashedOut: 'already_cashed_out',
  TooLate: 'too_late',
  Insufficient: 'insufficient_funds',
  StakeOutOfRange: 'stake_out_of_range',
  InvalidAutoCashout: 'invalid_auto_cashout',
  InvalidAvatar: 'invalid_avatar',
  LimitExceeded: 'limit_exceeded',
  BettingBlocked: 'betting_blocked',
  RateLimited: 'rate_limited',
  BadRequest: 'bad_request',
  Internal: 'internal_error',
  InvalidSlot: 'invalid_slot',
  NextRoundUnavailable: 'next_round_unavailable',
  InvalidLanguage: 'invalid_language',
  WalletUnavailable: 'wallet_unavailable',
  /** Too little of the betting window left to buy the stake; no wallet was asked. */
  BettingWindowClosing: 'betting_window_closing',
  /** This slot has been cancelled its limit of times this round; it opens next round. */
  CancelLimitReached: 'cancel_limit_reached',
  LaunchExpired: 'launch_expired',
} as const;

export type RoundPhase = 'betting' | 'running' | 'crashed';

/**
 * How many parallel stakes one player may hold in a round. Mirrors
 * protocol.SlotCount; the server validates against its own copy, so this one
 * only decides how many panels are drawn.
 */
export const SLOT_COUNT = 2;

/**
 * How many avatars exist. Mirrors protocol.AvatarCount; the server validates a
 * chosen avatar against its own copy, and migration 0008 carries the same bound
 * as a CHECK. The drawings themselves are in ui/avatars.ts.
 */
export const AVATAR_COUNT = 20;

/** A bet slot: 1 or 2. */
export type Slot = 1 | 2;

export const SLOTS: readonly Slot[] = [1, 2];

export interface Envelope {
  type: string;
  data?: unknown;
}

export interface RoundBetting {
  roundId: string;
  nonce: number;
  commitHash: string;
  phase: RoundPhase;
  bettingEndsAt: number;
  serverTime: number;
  nextNonce: number;
  nextCommitHash: string;
  /** The round a pre-bet lands in. Needed to tell "this round" from "the next". */
  nextRoundId: string;
}

export interface RoundStart {
  roundId: string;
  startedAt: number;
  serverTime: number;
  doublingSeconds: number;
  tickMs: number;
}

export interface RoundTick {
  roundId: string;
  multiplierX100: number;
  elapsedMs: number;
}

export interface RoundCrash {
  roundId: string;
  nonce: number;
  crashX100: number;
  serverSeed: string;
  commitHash: string;
  /**
   * The return to player this round was sealed with, as an exact fraction.
   *
   * Part of the proof, not a report of a setting: the crash point is derived from
   * it, so re-deriving a round with the current value instead of the round's own
   * gives the wrong answer for every round sealed before a change. The mirror of
   * `protocol.RoundCrash` in Go, and the input `game/fair.ts` needs.
   */
  rtpNum: number;
  rtpDen: number;
  nextRoundIn: number;
}

export interface BetPlacedFeed {
  roundId: string;
  nickname: string;
  /** Which of the twenty drawings to show beside the masked name. */
  avatarId: number;
  slot: number;
  amount: string;
  currency: string;
  playerCount: number;
}

export interface BetCashoutFeed {
  roundId: string;
  nickname: string;
  slot: number;
  multiplierX100: number;
  payout: string;
  currency: string;
}

/**
 * `nextRound` marks a stake taken for the round after the one on screen. The
 * money left the wallet at that moment, not when that round starts, which is
 * what the panel has to show.
 */
export interface BetAccepted {
  betId: string;
  roundId: string;
  slot: number;
  nextRound?: boolean;
  amount: string;
  autoCashoutX100?: number;
  balance: string;
}

export interface BetCanceled {
  betId: string;
  roundId: string;
  slot: number;
  nextRound?: boolean;
  balance: string;
  /**
   * How many more times this slot may be taken back in `roundId`, after this
   * cancellation. Always present, because zero is the value that matters: it is
   * what closes the slot until the next round.
   */
  cancelsLeft: number;
  /**
   * The stake is void but the refund has not reached the player's casino yet, so
   * `balance` is not an answer and must be ignored -- it carries a zero. Same
   * contract as CashedOut.walletPending; the wallet update that follows moves the
   * figure. Only ever set for a player who arrived through an operator.
   */
  walletPending?: boolean;
}

export interface CashedOut {
  betId: string;
  roundId: string;
  slot: number;
  multiplierX100: number;
  payout: string;
  balance: string;
  auto: boolean;
  /**
   * The win is settled and the balance is not in yet.
   *
   * Only ever true for a player who came in through an operator: their money is
   * at that casino, so the payout is recorded here first and handed over in a
   * second call. When it is set, `balance` is meaningless -- the client says the
   * payout is being credited and waits for a wallet.update rather than showing a
   * figure it knows to be stale.
   */
  walletPending?: boolean;
}

// --- client -> server ---
//
// Only `slot`, the stake and the optional auto cash-out target are ours to send.
// There is deliberately no bet id, round id or multiplier in any of these.

export interface BetPlaceRequest {
  slot: Slot;
  nextRound?: boolean;
  amount: string;
  autoCashoutX100?: number;
}

export interface BetCancelRequest {
  slot: Slot;
  nextRound?: boolean;
}

export interface BetCashOutRequest {
  slot: Slot;
}

export interface WalletUpdate {
  balance: string;
  currency: string;
}

export interface ProtocolError {
  code: string;
  message: string;
  /**
   * The slot a failed bet action was for. Absent when the failure belongs to no
   * slot, in which case no button is waiting on it.
   */
  slot?: number;
}

/** An exact fraction. The wire form of `fair.RTP`. */
export interface RTP {
  num: number;
  den: number;
}

export interface RoundResult {
  nonce: number;
  crashX100: number;
  commitHash: string;
  serverSeed: string;
  crashedAt: string;
  /**
   * The RTP this round used. Optional on the type because a result that came from
   * a cached response or an older client has none, and the derivation falls back
   * to the default -- which is what every round before the setting existed used.
   */
  rtp?: RTP;
}

export interface SnapshotBet {
  betId: string;
  slot: number;
  amount: string;
  currency: string;
  autoCashoutX100?: number;
  settled: boolean;
  /**
   * What a settled stake was paid at; absent for one still in play. The panel
   * needs it after a reconnect: without it the label fell back to the live
   * multiplier, so reloading after a cash-out read "cashed out at 1.43x" for a
   * bet paid at 1.01x.
   */
  cashedOutX100?: number;
}

/** One slot's remaining cancel allowance. The slot is carried, never implied. */
export interface SlotBudget {
  slot: number;
  left: number;
}

export interface Snapshot {
  phase: RoundPhase;
  roundId: string;
  nonce: number;
  commitHash: string;
  multiplierX100: number;
  elapsedMs: number;
  bettingEndsAt?: number;
  startedAt?: number;
  serverTime: number;
  nextRoundId: string;
  nextNonce: number;
  nextCommitHash: string;
  doublingSeconds: number;
  tickMs: number;
  /**
   * The whole cancel allowance per slot per round. Needed to open a round no
   * figures have arrived for yet: when a round is promoted, the one after it is
   * new and nobody can have cancelled a stake in a round that did not exist.
   */
  maxCancelsPerSlot: number;
  playerCount: number;
  history: RoundResult[];
  /** The caller's stakes in the round on screen, at most one per slot. */
  myBets: SnapshotBet[];
  /** The caller's stakes already placed on the round after it. */
  myNextBets: SnapshotBet[];
  /**
   * How many times each slot may still be taken back, for the round on screen
   * and for the one after it. Absent for a viewer with no player behind them.
   */
  cancelsLeft?: SlotBudget[];
  nextCancelsLeft?: SlotBudget[];
}

export interface FeedEntry {
  nickname: string;
  avatarId: number;
  slot: number;
  amount: string;
  currency: string;
  cashedOutX100?: number;
  payout?: string;
}

export interface AccountState {
  status: string;
  kycStatus: string;
  canBet: boolean;
  blockedReason?: string;
  selfExcludedUntil?: string;
}

export interface Limits {
  minBet: string;
  maxBet: string;
  /** The most a single bet can pay, from the currency row. Shown, and enforced. */
  maxPayout: string;
  singleStakeMax?: string;
  dailyStakeMax?: string;
  dailyStakeUsed: string;
  weeklyStakeMax?: string;
  weeklyStakeUsed: string;
  sessionReminderMinutes?: number;
}

/** One offered interface language. Mirrors i18n.Language. */
export interface SupportedLanguage {
  tag: string;
  name: string;
  rtl?: boolean;
}

/**
 * The language part of the handshake. The server resolves the tag -- a stored
 * preference beats the browser's guess and only the server can see it -- and
 * sends `rtl` as a fact so the two sides cannot disagree about layout direction.
 */
export interface LocaleInfo {
  language: string;
  rtl: boolean;
  /** True when the tag came from the player's saved setting, not from a header. */
  chosen: boolean;
  supported: SupportedLanguage[];
}

/** The account-level settings the menu edits. */
export interface Preferences {
  sound: boolean;
  music: boolean;
}

export interface SessionResponse {
  /**
   * Which kind of session this is. "operator" means the player arrived through a
   * B2B integration and is inside that casino's page: their account, their
   * balance and their responsible-gambling controls are all the operator's, so
   * ours are hidden rather than shown broken.
   */
  mode: 'player' | 'operator';
  operator?: { code: string; name: string };
  /**
   * Present only for a fun-mode player, and only ever set by the server: a demo
   * is entered by an operator launching one, never by anything this client can
   * do. `bankroll` is what the top-up button restores the play balance to.
   */
  demo?: { bankroll: string };
  player: { id: string; nickname: string; maskedNickname: string; avatarId: number };
  wallet: {
    balance: string;
    currency: string;
    scale: number;
    isCrypto: boolean;
    symbol: string;
  };
  account: AccountState;
  limits: Limits;
  locale: LocaleInfo;
  preferences: Preferences;
  game: Snapshot;
  feed: FeedEntry[];
  realtime: { url: string; ticket: string };
}

export function encode(type: string, data?: unknown): string {
  return data === undefined ? JSON.stringify({ type }) : JSON.stringify({ type, data });
}
